import React, { useEffect, useRef, useState, useMemo } from "react";
import PropTypes from "prop-types";
import { pathKeys } from "../constants";
import _ from "lodash";
import { useHistory } from "react-router-dom";
import moment from "moment";
import types from "../constants/types";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@material-ui/core";
import Tour from "reactour";
import { getIsGuideByKey, GUIDE_TYPE_ENUM, setIsGuideByKey } from "utils/helper";
import { useSelector } from "react-redux";
import { selectGuideListByKey } from "stores/config/config.selector";
import { useTranslation } from "react-i18next";
import LoadingWrapper from "./LoadingWrapper";
import { X } from "lucide-react";
import styles from "./ListFanpage.module.scss";
import cn from "classnames";

const AVATAR_COLORS = ['#16A34A','#2563EB','#7C3AED','#DB2777','#D97706','#0D9488','#DC2626','#4F46E5'];
function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 997;
  return AVATAR_COLORS[h % AVATAR_COLORS.length];
}

function fmt(n) {
  return n ? n.toLocaleString("vi-VN") : "0";
}

function relDays(iso, t) {
  const diff = Math.floor((new Date() - new Date(iso)) / 86400000);
  if (diff <= 0) return t('fanpage_page.rel_today');
  if (diff === 1) return t('fanpage_page.rel_yesterday');
  return t('fanpage_page.rel_days_ago', { count: diff });
}

export default function EnhancedTable(props) {
  const { t } = useTranslation();
  const { data, handleGetData, getData, user } = props;
  
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);
  const [search, setSearch] = useState("");
  const [sortValue, setSortValue] = useState("totalCus,desc");
  const [filterGroup, setFilterGroup] = useState("all");

  const rawRows = useMemo(() => {
    let all = (data && data?.content) || [];
    if (filterGroup === 'active')    all = all.filter(r => r.totalCus > 0);
    if (filterGroup === 'unscanned') all = all.filter(r => !r.lastScanTime);
    if (filterGroup === 'expired')   all = all.filter(r => r.status === 'inactive');

    if (sortValue === 'name,asc') {
      all = [...all].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'vi', { sensitivity: 'base' }));
    }

    return all;
  }, [data, filterGroup, sortValue]);
  
  const history = useHistory();

  const tourOverview = useSelector(selectGuideListByKey(GUIDE_TYPE_ENUM.fanpage_manager));
  const [tourConfig, setTourConfig] = useState(null);

  const [openPopupScanCustomer, setOpenPopupScanCustomer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [toastMsg, setToastMsg] = useState(null);
  const toastTimer = useRef(null);

  const [showExtNotice, setShowExtNotice] = useState(true);

  useEffect(() => {
    fetchData(page, rowsPerPage, search, sortValue, filterGroup);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (getIsGuideByKey(GUIDE_TYPE_ENUM.fanpage_manager) && tourOverview?.length) {
      setTourConfig(tourOverview);
      setIsGuideByKey(GUIDE_TYPE_ENUM.fanpage_manager);
    }
  }, [tourOverview]);

  const fetchData = (p, size, q, sort, group) => {
    const fg = group !== undefined ? group : filterGroup;
    let searchParts = [];
    if (q) searchParts.push(`name|id==${q}`);
    if (fg === 'active') searchParts.push('totalCus=gt=0');
    if (fg === 'unscanned') searchParts.push('lastScanTime=nexists=true');
    if (fg === 'expired') searchParts.push('status==inactive');
    const params = {
      page: p,
      size: size,
      sort: sort === 'name,asc' ? 'totalCus,desc' : sort,
      ...(searchParts.length ? { search: searchParts.join(';') } : {}),
    };
    handleGetData(params);
  };

  const handleSortChange = (e) => {
    const val = e.target.value;
    setSortValue(val);
    fetchData(page, rowsPerPage, search, val);
  };

  const handleSearchSubmit = () => {
    setPage(0);
    fetchData(0, rowsPerPage, search, sortValue);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") handleSearchSubmit();
  };
  
  let searchTimer = null;
  const handleSearchInput = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer);
    searchTimer = setTimeout(() => {
      setPage(0);
      fetchData(0, rowsPerPage, val, sortValue);
    }, 400);
  };

  const handleSubmitOpenExtension = () => {
    let url = types.URL.FACEBOOK + `&token=${localStorage.getItem("token") || null}` + "&tab=scan";
    window.open(url, "_blank");
    setOpenPopupScanCustomer(false);
  };

  const handleClickFanpage = (id) => {
    history.push({
      pathname: pathKeys.CUSTOMER,
      search: `?page=0&size=10&pageId=${id}&sort=createdAt%2Cdesc`
    });
  };

  const handleClickExtension = (url) => {
    window.open(url, "_blank");
    setOpenPopupScanCustomer(false);
  };

  const handleToggleScanCustomer = () => {
    setOpenPopupScanCustomer(!openPopupScanCustomer);
  };

  const syncPages = () => {
    setIsSyncing(true);
    fetchData(page, rowsPerPage, search, sortValue);
    setTimeout(() => setIsSyncing(false), 1200);
  };

  const copyText = (text, ev) => {
    ev.stopPropagation();
    if (navigator.clipboard) {
      navigator.clipboard.writeText(String(text));
      setToastMsg(t('fanpage_page.copy_success', { id: text }));
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToastMsg(null), 2500);
    }
  };

  const kpiStats = useMemo(() => {
    const s = data?.statistic || {};
    return {
      total:     s.totalPages           ?? data?.totalElements ?? 0,
      cust:      s.totalCus             ?? 0,
      unscanned: s.totalPagesNoScan     ?? 0,
      expired:   s.totalPagesInactive   ?? 0,
      haveCus:   s.totalPagesHaveCus    ?? 0,
    };
  }, [data]);

  const handleFilterChange = (group) => {
    setFilterGroup(group);
    setPage(0);
    fetchData(0, rowsPerPage, search, sortValue, group);
  };

  return (
    <>
      <Tour
        steps={tourConfig}
        isOpen={tourConfig != null}
        closeWithMask={false}
        lastStepNextButton={
          <Button size="small" className="btn--rounder" variant="contained">
            {t("common.btn.close")}
          </Button>
        }
        accentColor="#05ae34"
        onRequestClose={() => setTourConfig(null)}
      />
      
      <div className={styles.container}>
        
        {/* PAGE HEAD */}
        <div className={styles.pageHead}>
          <div>
            <div className={styles.pageTitle}>{t('fanpage_page.title')}</div>
            <div className={styles.pageSub}>{t('fanpage_page.sub')}</div>
          </div>
          <div className={styles.headActions}>
            <button className={cn(styles.btnOutlineLg, { [styles.syncing]: isSyncing })} onClick={syncPages}>
              <svg viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7m0 0V3.5M2 7h3.5"/></svg>
              <span>{isSyncing ? t('fanpage_page.syncing_btn') : t('fanpage_page.sync_btn')}</span>
            </button>
            <button id="scan_customer" className={styles.btnPrimaryLg} onClick={handleToggleScanCustomer}>
              <svg viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5"/><path d="M13.5 13.5L10.3 10.3"/></svg>
              {t('fanpage_page.scan_btn')}
            </button>
          </div>
        </div>

        {/* EXTENSION NOTICE */}
        {showExtNotice && (
          <div className={styles.extNotice}>
            <svg viewBox="0 0 16 16" fill="none">
              <path d="M8 1.5L1.5 13h13L8 1.5z" /><path d="M8 6v3M8 11.5h.01" />
            </svg>
            <div className={styles.extNoticeTxt}>
              <b>{t('fanpage_page.ext_notice')}</b> {t('fanpage_page.ext_notice_detail')}{' '}
              <a href="https://hotro.bombot.vn/knowledgebase/huong-dan-cai-dat-phien-ban-moi-ung-dung-gui-tin-hang-loat-bombot-vn/" target="_blank" rel="noopener noreferrer">{t('fanpage_page.ext_guide')}</a>
            </div>
            <div className={styles.extActions}>
              <button className={cn(styles.btnExt, styles.btnExtPrimary)} onClick={() => handleClickExtension(types.URL.EXTENSION)}>
                {t('fanpage_page.ext_install')}
              </button>
              <button className={cn(styles.btnExt, styles.btnExtGhost)} onClick={() => setShowExtNotice(false)}>
                {t('fanpage_page.ext_installed')}
              </button>
            </div>
          </div>
        )}

        {/* KPI ROW */}
        <div className={styles.kpiRow}>
          <div className={styles.kpiCard} onClick={() => handleFilterChange('all')}>
            <div className={styles.kpiTop}>
              <div className={cn(styles.kpiIcon, styles.kpiIconTotal)}>
                <svg viewBox="0 0 16 16" fill="none" strokeWidth="2"><path d="M4 2v12M4 2h8l-1.6 2.8L12 7.6H4"/></svg>
              </div>
              <span className={styles.kpiHint}>{t('fanpage_page.filter_all')}</span>
            </div>
            <div className={styles.kpiVal}>{kpiStats.total}</div>
            <div className={styles.kpiLbl}>{t('fanpage_page.kpi_total')}</div>
          </div>
          <div className={styles.kpiCard} onClick={() => handleFilterChange('active')}>
            <div className={styles.kpiTop}>
              <div className={cn(styles.kpiIcon, styles.kpiIconCust)}>
                <svg viewBox="0 0 16 16" fill="none" strokeWidth="2"><circle cx="6" cy="6" r="2.3"/><path d="M2 13.5c0-2.2 1.8-3.8 4-3.8s4 1.6 4 3.8"/><path d="M10.2 2.4a2.3 2.3 0 010 4.5M13.5 13.5c0-1.9-1.1-3.3-2.6-3.8"/></svg>
              </div>
              <span className={styles.kpiHint}>{t('fanpage_page.filter_active')}</span>
            </div>
            <div className={styles.kpiVal}>{fmt(kpiStats.cust)}</div>
            <div className={styles.kpiLbl}>{t('fanpage_page.kpi_customers')}</div>
          </div>
          <div className={styles.kpiCard} onClick={() => handleFilterChange('unscanned')}>
            <div className={styles.kpiTop}>
              <div className={cn(styles.kpiIcon, styles.kpiIconScan)}>
                <svg viewBox="0 0 16 16" fill="none" strokeWidth="2"><circle cx="7" cy="7" r="4.5"/><path d="M13.5 13.5L10.3 10.3"/></svg>
              </div>
              <span className={styles.kpiHint}>{t('fanpage_page.filter_unscanned')}</span>
            </div>
            <div className={styles.kpiVal}>{kpiStats.unscanned}</div>
            <div className={styles.kpiLbl}>{t('fanpage_page.kpi_unscanned')}</div>
          </div>
          <div className={cn(styles.kpiCard, styles.kpiAlert)} onClick={() => handleFilterChange('expired')}>
            <div className={styles.kpiTop}>
              <div className={cn(styles.kpiIcon, styles.kpiIconWarn)}>
                <svg viewBox="0 0 16 16" fill="none" strokeWidth="2"><path d="M8 1.5L1.5 13h13L8 1.5z"/><path d="M8 6.5v3M8 11.5h.01"/></svg>
              </div>
              <span className={styles.kpiHint}>{t('fanpage_page.filter_expired')}</span>
            </div>
            <div className={styles.kpiVal}>{kpiStats.expired}</div>
            <div className={styles.kpiLbl}>{t('fanpage_page.kpi_expired')}</div>
          </div>
        </div>

        <div className={styles.card}>
          {/* TOOLBAR */}
          <div className={styles.toolbar}>
            <div className={styles.searchWrap}>
              <svg viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5"/><path d="M13.5 13.5L10.3 10.3"/></svg>
              <input 
                id="search_page"
                className={styles.searchInput} 
                type="text" 
                placeholder={t('fanpage_page.search_placeholder')}
                value={search}
                onChange={handleSearchInput}
                onKeyDown={handleSearchKeyDown}
              />
            </div>
            <div className={styles.toolbarSpacer}></div>
            <select className={styles.selectSort} value={sortValue} onChange={handleSortChange}>
              <option value="totalCus,desc">{t('fanpage_page.sort_most_customers')}</option>
              <option value="name,asc">{t('fanpage_page.sort_name_az')}</option>
              <option value="lastScanTime,desc">{t('fanpage_page.sort_recent_scan')}</option>
            </select>
          </div>

          {/* FILTER CHIPS */}
          <div className={styles.filterRow}>
            <div className={cn(styles.chip, { [styles.chipOn]: filterGroup === 'all' })} onClick={() => handleFilterChange('all')}>
              {t('fanpage_page.filter_all')}
              <span className={styles.chipCount}>{kpiStats.total}</span>
            </div>
            <div className={cn(styles.chip, { [styles.chipOn]: filterGroup === 'active' })} onClick={() => handleFilterChange('active')}>
              {t('fanpage_page.filter_active')}
              <span className={styles.chipCount}>{kpiStats.haveCus}</span>
            </div>
            <div className={cn(styles.chip, { [styles.chipOn]: filterGroup === 'unscanned' })} onClick={() => handleFilterChange('unscanned')}>
              {t('fanpage_page.filter_unscanned')}
              <span className={styles.chipCount}>{kpiStats.unscanned}</span>
            </div>
            <div className={cn(styles.chip, { [styles.chipOn]: filterGroup === 'expired' })} onClick={() => handleFilterChange('expired')}>
              {t('fanpage_page.filter_expired')}
              <span className={styles.chipCount}>{kpiStats.expired}</span>
            </div>
            {filterGroup !== 'all' && (
              <span className={cn(styles.filterClear, styles.filterClearShow)} onClick={() => handleFilterChange('all')}>
                {t('fanpage_page.filter_clear')}
              </span>
            )}
          </div>

          {/* TABLE */}
          <div className={styles.tblScroll}>
            {rawRows.length > 0 ? (
              <table className={styles.customTable}>
                <thead>
                  <tr>
                    <th>{t('fanpage_page.col_fanpage')}</th>
                    <th>{t('fanpage_page.col_status')}</th>
                    <th>{t('fanpage_page.col_customers')}</th>
                    <th>{t('fanpage_page.col_last_scan')}</th>
                    <th className={styles.thRight}>{t('fanpage_page.col_action')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rawRows.map((r, i) => {
                    const isUnscanned = !r.totalCus || r.totalCus === 0;
                    const isInactive = r.status === 'inactive';
                    const scanTime = r.lastScanTime || r.updatedAt;
                    return (
                      <tr key={i} className={styles.pageRow} onClick={() => handleClickFanpage(r.id)}>
                        <td>
                          <div className={styles.fpCell}>
                            <div className={styles.fpAvatar} style={{ background: getAvatarColor(r.name) }}>
                              {r.name ? r.name.charAt(0).toUpperCase() : '?'}
                            </div>
                            <div className={styles.fpInfo}>
                              <span className={styles.fpName} title={r.name}>{r.name}</span>
                              <span className={styles.fpIdRow}>
                                <span className={styles.fpId}>{r.id}</span>
                                <button className={styles.copyBtn} title={t('common.btn.copy')} onClick={(e) => copyText(r.id, e)}>
                                  <svg viewBox="0 0 12 12" fill="none"><rect x="3" y="3" width="7" height="7" rx="1"/><path d="M2 8V2h6"/></svg>
                                </button>
                              </span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={cn(styles.statusBadge, isInactive ? styles.statusExpired : styles.statusOk)}>
                            <span className={styles.statusDot}></span>
                            {isInactive ? t('fanpage_page.status_inactive') : t('fanpage_page.status_active')}
                          </span>
                        </td>
                        <td>
                          {isUnscanned ? (
                            <span className={styles.custZero}>{t('fanpage_page.no_customers')}</span>
                          ) : (
                            <div className={styles.custCell}>
                              <span className={styles.custVal}>{fmt(r.totalCus)}</span>
                            </div>
                          )}
                        </td>
                        <td>
                          {scanTime ? (
                            <div className={styles.timeCell}>
                              <span className={styles.timeMain}>{relDays(scanTime, t)}</span>
                              <span className={styles.timeSub}>{moment(scanTime).format("HH:mm · DD/MM/YYYY")}</span>
                            </div>
                          ) : (
                            <span className={styles.timeNever}>{t('fanpage_page.never_scanned')}</span>
                          )}
                        </td>
                        <td className={styles.tdRight}>
                          <div className={styles.actionCell}>
                            {isUnscanned ? (
                              <button className={styles.btnScan} onClick={(e) => { e.stopPropagation(); handleToggleScanCustomer(); }}>
                                <svg viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="4.5"/><path d="M13.5 13.5L10.3 10.3"/></svg>{t('fanpage_page.scan_now')}
                              </button>
                            ) : (
                              <>
                                <button className={styles.btnScan} onClick={(e) => { e.stopPropagation(); window.open(types.URL.FACEBOOK + `&token=${localStorage.getItem('token') || null}`, '_blank'); }}>
                                  <svg viewBox="0 0 14 14" fill="none"><path d="M2 7l10-5-3.5 10L6 8 2 7z"/></svg>{t('fanpage_page.create_campaign')}
                                </button>
                                <button className={styles.btnScanAgain} title={t('fanpage_page.scan_again')} onClick={(e) => { e.stopPropagation(); handleToggleScanCustomer(); }}>
                                  <svg viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7m0 0V3.5M2 7h3.5"/></svg>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            ) : (
              <div className={styles.tblEmpty}>
                <div className={styles.tblEmptyIcon}>
                  <svg viewBox="0 0 20 20" fill="none"><path d="M4 2v16M4 2h10l-2 3.5 2 3.5H4"/></svg>
                </div>
                <div className={styles.tblEmptyTitle}>{t('fanpage_page.empty_title')}</div>
                <div className={styles.tblEmptySub}>{t('fanpage_page.empty_sub')}</div>
                <button className={styles.btnCreateSm} onClick={syncPages}>
                  <svg viewBox="0 0 14 14" fill="none"><path d="M12 7A5 5 0 112 7m0 0V3.5M2 7h3.5"/></svg>
                  {t('fanpage_page.sync_btn')}
                </button>
              </div>
            )}
          </div>

          {/* PAGINATION */}
          <div className={styles.tblFooter}>
            <span className={styles.tblCount}>
              {t('fanpage_page.per_page')} <b>{page * rowsPerPage + 1}–{Math.min((page + 1) * rowsPerPage, data?.totalElements || 0)}</b> / <b>{fmt(data?.totalElements || 0)}</b> fanpage
            </span>
            <div className={styles.perPage}>
              <span>{t('fanpage_page.per_page')}</span>
              <select className={styles.selectSort} value={rowsPerPage} onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setPage(0);
              }}>
                <option value={8}>8</option>
                <option value={15}>15</option>
                <option value={30}>30</option>
              </select>
              <span>{t('fanpage_page.per_page_unit')}</span>
            </div>
            
            <div className={styles.pagination}>
              {(() => {
                const totalPages = Math.max(1, Math.ceil((data?.totalElements || 0) / rowsPerPage));
                const current = page + 1;
                
                let pages = [];
                if (totalPages <= 7) pages = Array.from({length: totalPages}, (_,i) => i+1);
                else {
                  pages = [1];
                  if (current > 3) pages.push('…');
                  for (let i = Math.max(2, current-1); i <= Math.min(totalPages-1, current+1); i++) pages.push(i);
                  if (current < totalPages - 2) pages.push('…');
                  pages.push(totalPages);
                }
                
                return (
                  <>
                    <button className={styles.pgBtn} disabled={current === 1} onClick={() => setPage(page - 1)}>‹</button>
                    {pages.map((x, idx) => x === '…' 
                      ? <span key={idx} className={styles.pgEllipsis}>…</span>
                      : <button key={idx} className={cn(styles.pgBtn, { [styles.pgBtnOn]: x === current })} onClick={() => setPage(x - 1)}>{x}</button>
                    )}
                    <button className={styles.pgBtn} disabled={current === totalPages} onClick={() => setPage(page + 1)}>›</button>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      
      {/* EXTENSION MODAL */}
      <Dialog open={openPopupScanCustomer} maxWidth="xs" classes={{ paper: styles.dialog }}>
        <LoadingWrapper isOpen={isLoading}>
          <DialogTitle className={styles.dialogTitle}>
            <IconButton aria-label="close" className={styles.closeButton} onClick={handleToggleScanCustomer} style={{ position: "absolute", right: 8, top: 8 }}>
              <X />
            </IconButton>
          </DialogTitle>
          <DialogContent style={{ marginTop: "10px", lineHeight: '23px', padding: 24 }}>
            {t("common.btn.scan_customer_dialog_1")}{" "}
            <a
              href="https://hotro.bombot.vn/knowledgebase/huong-dan-cai-dat-phien-ban-moi-ung-dung-gui-tin-hang-loat-bombot-vn/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#06ae34", textDecoration: "none", fontWeight: 600, borderBottom: "1px solid #06ae34" }}
              onClick={() => setOpenPopupScanCustomer(false)}
            >
              {t("common.btn.see_detail")}
            </a>{" "}
            {t("common.btn.scan_customer_dialog_2")}
          </DialogContent>
          <DialogActions style={{ display: "flex", justifyContent: "space-between", padding: 16 }}>
            <Button
              onClick={() => handleClickExtension(types.URL.EXTENSION)}
              style={{ backgroundColor: "white", color: "#05ae34", border: "1px solid #05ae34", width: "100%", textTransform: "none", fontWeight: 600 }}
            >
              {t("common.btn.install_download_file")}
            </Button>
            <Button
              onClick={handleSubmitOpenExtension}
              style={{ backgroundColor: "#05ae34", color: "white", width: "100%", textTransform: "none", fontWeight: 600 }}
              variant="contained"
            >
              {t("common.btn.continue")}
            </Button>
          </DialogActions>
        </LoadingWrapper>
      </Dialog>

      {/* Toast Copy */}
      <div className={`${styles.toast} ${toastMsg ? styles.show : ''}`}>
        <svg viewBox="0 0 14 14" fill="none"><path d="M2.5 7.5l3 3 6-6.5" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
        {toastMsg}
      </div>
    </>
  );
}

EnhancedTable.propTypes = {
  data: PropTypes.object,
  search: PropTypes.string,
  isSearch: PropTypes.bool,
  isPagination: PropTypes.bool,
  getData: PropTypes.func,
  handleGetData: PropTypes.func
};

EnhancedTable.defaultProps = {
  isPagination: true,
  isSearch: true,
  handleGetData: (f) => f,
  getData: (f) => f,
  data: {},
  search: null
};
